import React, { useState, useEffect } from 'react';
import { MdClose, MdAdd, MdEdit, MdDelete, MdSave, MdCancel } from 'react-icons/md';
import api from '../../services/api';
import { useUser } from '../../contexts/userContext';
import './style.css';

function ModalInvestimentos({ isOpen, onClose }) {
  const { user } = useUser();
  const [investimentos, setInvestimentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingInvestimento, setEditingInvestimento] = useState(null);

  // Estados do formulário
  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    origem: '',
    dataInicial: '',
    dataFinal: '',
    observacoes: ''
  });

  useEffect(() => {
    if (isOpen && user?.empresa_id) {
      buscarInvestimentos();
    }
  }, [isOpen, user?.empresa_id]);

  const buscarInvestimentos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/marketing/investimentos', {
        params: { empresaId: user.empresa_id }
      });
      setInvestimentos(response.data);
    } catch (error) {
      console.error('Erro ao buscar investimentos:', error);
      alert('Erro ao carregar investimentos');
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      descricao: '',
      valor: '',
      origem: '',
      dataInicial: '',
      dataFinal: '',
      observacoes: ''
    });
    setEditingInvestimento(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.descricao || !formData.valor || !formData.origem) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    const investimentoData = {
      ...formData,
      valor: parseFloat(formData.valor),
      empresaId: user.empresa_id
    };

    try {
      if (editingInvestimento) {
        await api.put(`/marketing/investimentos/${editingInvestimento.id}`, investimentoData);
        alert('Investimento atualizado com sucesso!');
      } else {
        await api.post('/marketing/investimentos', investimentoData);
        alert('Investimento criado com sucesso!');
      }
      
      resetForm();
      buscarInvestimentos();
    } catch (error) {
      console.error('Erro ao salvar investimento:', error);
      alert('Erro ao salvar investimento');
    }
  };

  const handleEdit = (investimento) => {
    setFormData({
      descricao: investimento.descricao,
      valor: investimento.valor.toString(),
      origem: investimento.origem,
      dataInicial: investimento.data_inicial?.split('T')[0] || '',
      dataFinal: investimento.data_final?.split('T')[0] || '',
      observacoes: investimento.observacoes || ''
    });
    setEditingInvestimento(investimento);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este investimento?')) {
      return;
    }

    try {
      await api.delete(`/marketing/investimentos/${id}`);
      alert('Investimento excluído com sucesso!');
      buscarInvestimentos();
    } catch (error) {
      console.error('Erro ao excluir investimento:', error);
      alert('Erro ao excluir investimento');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-investimentos-overlay">
      <div className="modal-investimentos-container">
        <div className="modal-investimentos-header">
          <h2>Investimentos em Marketing</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        <div className="modal-investimentos-content">
          {!showForm ? (
            <>
              <div className="investimentos-actions">
                <button 
                  className="btn-add-investimento" 
                  onClick={() => setShowForm(true)}
                >
                  <MdAdd /> Novo Investimento
                </button>
              </div>

              <div className="investimentos-list">
                {loading ? (
                  <div className="loading-message">Carregando...</div>
                ) : investimentos.length === 0 ? (
                  <div className="empty-message">Nenhum investimento cadastrado</div>
                ) : (
                  investimentos.map((investimento) => (
                    <div key={investimento.id} className="investimento-item">
                      <div className="investimento-info">
                        <h4>{investimento.descricao}</h4>
                        <p><strong>Valor:</strong> {formatCurrency(investimento.valor)}</p>
                        <p><strong>Origem:</strong> {investimento.origem}</p>
                        <p><strong>Período:</strong> {formatDate(investimento.data_inicial)} - {formatDate(investimento.data_final)}</p>
                        {investimento.observacoes && (
                          <p><strong>Observações:</strong> {investimento.observacoes}</p>
                        )}
                      </div>
                      <div className="investimento-actions">
                        <button 
                          className="btn-edit" 
                          onClick={() => handleEdit(investimento)}
                          title="Editar"
                        >
                          <MdEdit />
                        </button>
                        <button 
                          className="btn-delete" 
                          onClick={() => handleDelete(investimento.id)}
                          title="Excluir"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <form className="investimento-form" onSubmit={handleSubmit}>
              <h3>{editingInvestimento ? 'Editar Investimento' : 'Novo Investimento'}</h3>
              
              <div className="form-group">
                <label>Descrição *</label>
                <input
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => handleInputChange('descricao', e.target.value)}
                  placeholder="Ex: Campanha Google Ads"
                  required
                />
              </div>

              <div className="form-group">
                <label>Valor *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.valor}
                  onChange={(e) => handleInputChange('valor', e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="form-group">
                <label>Origem *</label>
                <select
                  value={formData.origem}
                  onChange={(e) => handleInputChange('origem', e.target.value)}
                  required
                >
                  <option value="">Selecione a origem</option>
                  <option value="Google Ads">Google Ads</option>
                  <option value="Facebook Ads">Facebook Ads</option>
                  <option value="Instagram Ads">Instagram Ads</option>
                  <option value="LinkedIn Ads">LinkedIn Ads</option>
                  <option value="Email Marketing">Email Marketing</option>
                  <option value="SEO">SEO</option>
                  <option value="Mídia Impressa">Mídia Impressa</option>
                  <option value="Rádio">Rádio</option>
                  <option value="TV">TV</option>
                  <option value="Eventos">Eventos</option>
                  <option value="Outdoor">Outdoor</option>
                  <option value="Influenciadores">Influenciadores</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Data Inicial</label>
                  <input
                    type="date"
                    value={formData.dataInicial}
                    onChange={(e) => handleInputChange('dataInicial', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Data Final</label>
                  <input
                    type="date"
                    value={formData.dataFinal}
                    onChange={(e) => handleInputChange('dataFinal', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => handleInputChange('observacoes', e.target.value)}
                  placeholder="Informações adicionais sobre o investimento"
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  <MdCancel /> Cancelar
                </button>
                <button type="submit" className="btn-save">
                  <MdSave /> {editingInvestimento ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModalInvestimentos; 